import React, { useEffect, useMemo, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { formatDecimalString, toDecimalString } from '@/utils/decimal-display';
import { useMarkPaid } from '../hooks/use-mark-paid';
import { paidDiscrepancy } from '../utils/paid-discrepancy';
import { formatPeriodDates } from '../utils/statement-period';
import type { BillingStatementListItem } from '@/types';

interface Props {
  statement: BillingStatementListItem;
  onClose: () => void;
}

/** Local calendar date as `YYYY-MM-DD` — what the trader means by "today". */
const localToday = (): string => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

// Committed amount grammar: optional sign, digits, up to 2dp (money is cents).
const AMOUNT_VALID = /^-?\d+(\.\d{1,2})?$/;
// Mid-entry states the input tolerates while typing ("", "-", "12.", "-.").
const AMOUNT_INTERMEDIATE = /^-?\d*\.?\d{0,2}$/;

/**
 * Mark a billing statement paid (LR-556): settled date (defaults to today) and
 * the actual amount paid/received (defaults to total_accrued), both editable.
 * The amount stays a Decimal STRING end-to-end — the payload sends the user's
 * text verbatim, no float round-trip on money. Both fields are always sent
 * explicitly so the backend's UTC "today" default can't disagree with the
 * trader's local calendar.
 *
 * Visuals follow the billing design mock's confirm-dialog pattern: 456px
 * card, title + copy, a muted info panel, right-aligned Cancel + solid-green
 * confirm; money is signed green/red, discrepancies are amber.
 */
export const MarkPaidModal: React.FC<Props> = ({ statement, onClose }) => {
  // Consumption-boundary guard (HON-03): decimal fields are typed string but
  // can arrive as numbers at runtime — normalise before any .trim()/regex.
  const totalAccrued = toDecimalString(statement.total_accrued) ?? '0';
  const [paidDate, setPaidDate] = useState(localToday);
  const [amount, setAmount] = useState(totalAccrued);
  const dialogRef = useRef<HTMLDivElement>(null);
  // Backdrop close only when the CLICK STARTED on the backdrop — a text-drag
  // from the amount input released over the overlay must not discard the form.
  const mouseDownOnBackdrop = useRef(false);
  const { mutate, isPending, error } = useMarkPaid();

  // Focus the dialog on mount so Escape works without tabbing into it first;
  // restore focus to the opener on unmount (WCAG 2.4.3).
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => opener?.focus?.();
  }, []);

  // Minimal focus trap (aria-modal promises one): keep Tab / Shift+Tab cycling
  // inside the dialog instead of escaping to the background page. Disabled
  // elements are excluded — a disabled submit as `last` would never match
  // document.activeElement and the trap would leak past it.
  const trapTab = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const focusables = dialogRef.current?.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), [href], ' +
        'select:not([disabled]), textarea:not([disabled]), ' +
        '[tabindex]:not([tabindex="-1"])',
    );
    if (!focusables || focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === dialogRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const amountValid = AMOUNT_VALID.test(amount.trim());
  const canSubmit = paidDate !== '' && amountValid && !isPending;

  // Direction comes from the sign of total_accrued (positive = due to us).
  const isReceivable = !totalAccrued.startsWith('-');

  // Live discrepancy vs the accrued figure — string/BigInt math only.
  const discrepancy = useMemo(
    () => (amountValid ? paidDiscrepancy(totalAccrued, amount.trim()) : null),
    [amountValid, totalAccrued, amount],
  );

  const errorStatus = (error as { response?: { status?: number } } | null)
    ?.response?.status;
  const alreadyPaid = errorStatus === 409;
  // A 403 is a permissions problem — "check the date and amount" would lie.
  const permissionDenied = errorStatus === 403;

  const handleSubmit = () => {
    if (!canSubmit) return;
    mutate(
      {
        id: statement.id,
        params: { paid_date: paidDate, paid_amount: amount.trim() },
      },
      { onSuccess: onClose },
    );
  };

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/25 p-4"
      onMouseDown={(e) => {
        mouseDownOnBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (mouseDownOnBackdrop.current && e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Mark statement paid"
        tabIndex={-1}
        className="w-full max-w-[456px] overflow-hidden rounded-lg bg-white shadow-2xl focus:outline-none"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
          trapTab(e);
        }}
      >
        <div className="p-6">
          <div className="mb-2 text-lg font-semibold text-gray-900">
            Mark paid
          </div>
          <p className="mb-4 text-sm leading-relaxed text-gray-600">
            Record the settled date and the amount actually{' '}
            {isReceivable ? 'received' : 'paid'} for{' '}
            <b className="text-gray-900">{statement.counterparty_name}</b>. The
            statement moves to Paid.
          </p>

          <div className="mb-4 grid grid-cols-[96px_1fr] gap-x-3 gap-y-1.5 rounded-md border border-gray-200 bg-gray-50 px-3.5 py-3 text-xs">
            <span className="text-gray-500">Counterparty</span>
            <span className="text-gray-900">{statement.counterparty_name}</span>
            <span className="text-gray-500">Period</span>
            <span className="text-gray-900">
              {formatPeriodDates(statement.period_start, statement.period_end)}
            </span>
            <span className="text-gray-500">Accrued</span>
            <span
              className={clsx(
                'font-semibold tabular-nums',
                isReceivable ? 'text-green-700' : 'text-red-600',
              )}
            >
              {formatDecimalString(statement.total_accrued, {
                currency: statement.currency,
              })}{' '}
              <span className="font-normal text-gray-400">
                ({isReceivable ? 'to receive' : 'to pay'})
              </span>
            </span>
          </div>

          <div className="mb-4 flex flex-col gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-gray-600">
                Settled date
              </span>
              <input
                type="date"
                value={paidDate}
                onChange={(e) => setPaidDate(e.target.value)}
                className="rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-gray-600">
                {isReceivable ? 'Amount received' : 'Amount paid'} (
                {statement.currency})
              </span>
              <input
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={(e) => {
                  // Strip spaces always; strip commas ONLY when a dot is
                  // present — then they are thousands separators ("1,234.56").
                  // An EU-style "12,34" (comma as decimal mark) must NOT
                  // become 1234 (a 100x amount); the grammar rejects it and
                  // the input stays unchanged.
                  const raw = e.target.value.replace(/\s/g, '');
                  const v = raw.includes('.') ? raw.replace(/,/g, '') : raw;
                  if (AMOUNT_INTERMEDIATE.test(v)) setAmount(v);
                }}
                aria-invalid={amount !== '' && !amountValid}
                className="rounded-md border border-gray-300 px-2 py-1.5 text-sm tabular-nums focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <span className="text-xs text-gray-400">
                Defaults to the accrued total; adjust to what was actually{' '}
                {isReceivable ? 'received' : 'paid'}.
              </span>
            </label>
          </div>

          {discrepancy && (
            <div
              className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700"
              role="status"
            >
              Differs from accrued by{' '}
              <span className="font-semibold tabular-nums">
                {formatDecimalString(discrepancy, {
                  currency: statement.currency,
                })}
              </span>
            </div>
          )}

          {error && (
            <div className="mb-4 text-xs text-red-600">
              {alreadyPaid
                ? 'This statement is already marked paid.'
                : permissionDenied
                  ? 'You do not have permission to mark statements paid.'
                  : 'Failed to mark the statement paid. Check the date and amount.'}
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="rounded-md border border-green-600 bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-300"
            >
              {isPending ? 'Saving…' : 'Mark paid'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
